<template>
  <Modal
    hide-footer
    no-padding
    :class="{ 'share-sign-up-modal--on-image': imageUrl }"
  >
    <template #title>
      <!-- A glimpse of what sharing produces, over the partner's own image where they have one -->
      <div
        class="relative h-52 select-none overflow-hidden rounded-t-lg border-b"
        :class="imageUrl ? 'bg-white' : fallbackBackgrounds[option]"
        aria-hidden="true"
      >
        <img
          v-if="imageUrl"
          :src="imageUrl"
          alt=""
          class="absolute inset-0 size-full scale-105 object-cover opacity-75 blur-[5px]"
        />

        <ShareSignUpMockup
          :option="option"
          :image-url="imageUrl"
          :domain="previewDomain"
          :link="previewLink"
          :partner-name="partnerName"
        />
      </div>

      <Heading
        type="h4"
        class="flex items-center gap-2.5 px-8 pt-8 md:px-10 md:pt-10"
      >
        <ShareOptionIcon
          :type="option"
          class="text-xl"
        />
        {{ explainer.title }}
      </Heading>
    </template>

    <div class="mt-5 px-8 pb-8 md:px-10 md:pb-10">
      <p>{{ $t('share_sign_up_intro', 'Sign up and you\'ll get:') }}</p>
      <ul class="mt-3 space-y-2">
        <li
          v-for="feature in explainer.features"
          :key="feature"
          class="flex gap-3"
        >
          <Icon
            icon="check"
            class="mt-1 shrink-0 text-sm text-green-600"
          />
          {{ feature }}
        </li>
      </ul>

      <LoginOrRegisterForm
        :button-label="$t('get_my_website', 'Get my website')"
        source="brand-copilot"
        :partner-slug="partnerSlug"
        :redirect-to="redirectTo"
        button-color="brand"
        inline
        class="mt-6"
        @success="onSuccess"
      />

      <Markdown
        v-if="!hasSubmitted"
        as="p"
        class="mt-4 text-xs text-supplement"
        :text="$t('enter_email_legal')"
      />
    </div>
  </Modal>
</template>

<script setup lang="ts">
import type { LoginOrRegisterResponse } from '~/components/forms/LoginOrRegisterForm.vue'
import type { Media } from '~/types/media'
import type { ShareOption } from '~/types/partner'

const props = defineProps<{
  option: ShareOption
  partnerSlug: string
  partnerName: string
  redirectTo: string
  previewImage?: Media | null
  // The image Facebook would show for the link, preferred over the featured image
  previewImageUrl?: string | null
  previewDomain: string
  previewLink: string
}>()

const { t } = useI18n()
const { closeModal } = useModal()

type Explainer = { title: string, features: string[] }

// Describes what each option does once signed up, without fetching or drafting anything
const explainers = computed<Partial<Record<ShareOption, Explainer>>>(() => ({
  facebook: {
    title: t('share_on_facebook'),
    features: [
      t('facebook_drive_traffic_feature_1'),
      t('facebook_drive_traffic_feature_2'),
      t('share_sign_up_facebook_open', 'Copy it and open Facebook in one click')
    ]
  },
  instagram: {
    title: t('share_on_instagram'),
    features: [
      t('share_sign_up_instagram_modes', 'Share as a story or a post'),
      t('share_sign_up_instagram_images', 'Images ready to download, sized for each'),
      t('instagram_create_post_feature_1')
    ]
  },
  linkedin: {
    title: t('share_on_linkedin'),
    features: [
      t('share_sign_up_linkedin_post', 'A professional post written for you, with your link'),
      t('share_sign_up_linkedin_open', 'Copy it and open LinkedIn in one click')
    ]
  },
  whatsapp: {
    title: t('share_on_whatsapp'),
    features: [
      t('share_sign_up_whatsapp_send', 'Send your link to a client or group in one tap')
    ]
  },
  email: {
    title: t('share_by_email'),
    features: [
      t('share_sign_up_email_draft', 'A personal email written for you, with your link'),
      t('share_sign_up_email_copy', 'Copy it into any email and send')
    ]
  },
  qrcode: {
    title: t('share_with_qr_code', 'Share with a QR code'),
    features: [
      t('share_sign_up_qr_code_scan', 'A QR code clients scan to open your link'),
      t('share_qr_code_description')
    ]
  }
}))

const explainer = computed(() => explainers.value[props.option] ?? { title: t('share'), features: [] })

// The image Facebook would show for the link is truest to what they'll share
const imageUrl = computed(() => props.previewImageUrl || props.previewImage?.full_url || null)

const fallbackBackgrounds: Partial<Record<ShareOption, string>> = {
  facebook: 'bg-gradient-to-br from-blue-100 to-blue-300',
  instagram: 'bg-gradient-to-br from-amber-100 via-pink-200 to-purple-300',
  linkedin: 'bg-gradient-to-br from-sky-100 to-sky-300',
  whatsapp: 'bg-gradient-to-br from-green-100 to-emerald-300',
  email: 'bg-gradient-to-br from-purple-100 to-purple-300',
  qrcode: 'bg-gradient-to-br from-teal-100 to-teal-300'
}

const hasSubmitted = ref(false)

const onSuccess = ({ new_user }: LoginOrRegisterResponse) => {
  hasSubmitted.value = true

  // New accounts are signed in straight away and taken back to the page
  if (new_user) {
    closeModal()
  }
}
</script>

<style>
/* Readable on any partner image, light or dark */
.share-sign-up-modal--on-image .modal__close {
  @apply text-xs text-white/90 hover:text-white;
}

.share-sign-up-modal--on-image .modal__close::before {
  @apply absolute left-1/2 top-1/2 -z-10 size-8 -translate-x-1/2 -translate-y-1/2 rounded bg-gray-900/25 transition-colors content-[''];
}

.share-sign-up-modal--on-image .modal__close:hover::before {
  @apply bg-gray-900/40;
}
</style>
